import { Link } from 'react-router-dom';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Eyebrow, SectionHeader } from '@/components/Atoms';
import { WHY_HALLIE, FUNNEL_STEPS, BUSINESS_OUTCOMES } from '@/data/whyHallie';

// ─── Why Clients Work With Hallie ───
export function WhyClientsBlock() {
  return (
    <section className="py-24 lg:py-32" data-testid="why-clients-block">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
        <SectionHeader eyebrow="Why Clients Work With Hallie" title="Trust that comes" italic="from showing up well, every time." />
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {WHY_HALLIE.map(({ icon: Icon, title, body }) => (
            <div key={title} className="card-soft p-8">
              <Icon size={26} strokeWidth={1.25} className="text-[#C9A24D]" />
              <h3 className="font-serif-display text-2xl mt-5 leading-tight">{title}</h3>
              <p className="text-[#1C1C1C]/75 leading-relaxed mt-3 text-sm">{body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Business Outcomes (qualitative, no fake stats) ───
export function BusinessOutcomesBlock() {
  return (
    <section className="bg-[#FBF7F2] border-y border-[#E6CFC7]/70 py-24 lg:py-32" data-testid="business-outcomes-block">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
        <SectionHeader eyebrow="Outcomes" title="What clients walk away with" italic="when the work is done well." />
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E6CFC7]/60">
          {BUSINESS_OUTCOMES.map((o, i) => (
            <div key={o} className="bg-[#F4EFEA] p-8">
              <div className="num-marker">{String(i + 1).padStart(2, '0')}</div>
              <p className="mt-4 font-serif-display text-xl text-[#1C1C1C] leading-snug">{o}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Discovery Call Funnel ───
export function FunnelBlock() {
  return (
    <section className="bg-[#1C1C1C] text-[#F4EFEA] py-24 lg:py-32" data-testid="discovery-funnel-block">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
        <Eyebrow>How It Works</Eyebrow>
        <h2 className="font-serif-display text-4xl sm:text-5xl mt-5 leading-[1.05] tracking-tight max-w-3xl">
          From first inquiry<br/>
          <span className="font-serif-italic italic text-[#C9A24D]">to dependable implementation.</span>
        </h2>
        <ol className="mt-16 grid grid-cols-1 md:grid-cols-5 gap-px bg-[#F4EFEA]/15" data-testid="discovery-funnel-steps">
          {FUNNEL_STEPS.map((s) => (
            <li key={s.step} className="bg-[#1C1C1C] p-8 lg:p-10 flex flex-col">
              <div className="num-marker text-[#C9A24D]">{s.step}</div>
              <h3 className="font-serif-display text-xl lg:text-2xl mt-5 leading-tight">{s.title}</h3>
              <p className="text-sm text-[#E6CFC7]/85 leading-relaxed mt-3">{s.body}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

// ─── FAQ Block ───
export function FAQBlock({ eyebrow = 'FAQ', title = 'Common questions', italic = 'answered plainly.', items = [], testidPrefix = 'faq' }) {
  return (
    <section className="py-24 lg:py-32" data-testid={`${testidPrefix}-block`}>
      <div className="max-w-[1100px] mx-auto px-6 sm:px-10 lg:px-16">
        <SectionHeader eyebrow={eyebrow} title={title} italic={italic} />
        <div className="mt-14">
          <Accordion type="single" collapsible className="border-t border-[#E6CFC7]/70">
            {items.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-b border-[#E6CFC7]/70" data-testid={`${testidPrefix}-item-${i}`}>
                <AccordionTrigger className="text-left text-lg sm:text-xl font-serif-display hover:no-underline py-6">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#1C1C1C]/80 leading-relaxed text-base pb-6 pr-8">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

// ─── CTA Band (reusable) ───
export function CtaBand({ heading = "Let's build better systems", italic = 'together.', kicker, dark = false }) {
  if (dark) {
    return (
      <section className="bg-[#1C1C1C] text-[#F4EFEA] py-24 lg:py-32 text-center" data-testid="cta-band-dark">
        <div className="max-w-[1000px] mx-auto px-6">
          <Eyebrow className="mx-auto">Begin</Eyebrow>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05] tracking-tight">
            {heading}<br/><span className="font-serif-italic italic text-[#C9A24D]">{italic}</span>
          </h2>
          {kicker && <p className="mt-6 text-lg text-[#F4EFEA]/75 max-w-2xl mx-auto leading-relaxed">{kicker}</p>}
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="btn-ghost-light" data-testid="cta-band-book-call">Book a Discovery Call</Link>
            <Link to="/portfolio" className="text-sm uppercase tracking-[0.18em] text-[#F4EFEA]/70 hover:text-[#C9A24D] self-center transition-colors" data-testid="cta-band-portfolio">View Portfolio &rarr;</Link>
          </div>
        </div>
      </section>
    );
  }
  return (
    <section className="bg-[#F4EFEA] py-24 lg:py-32 border-t border-[#E6CFC7]/70 text-center" data-testid="cta-band-light">
      <div className="max-w-[1100px] mx-auto px-6 sm:px-10">
        <Eyebrow className="mx-auto">Begin</Eyebrow>
        <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05] tracking-tight">
          {heading}<br/><span className="font-serif-italic italic text-[#C9A24D]">{italic}</span>
        </h2>
        {kicker && <p className="mt-7 text-lg text-[#1C1C1C]/75 max-w-2xl mx-auto leading-relaxed">{kicker}</p>}
        <div className="mt-10 flex flex-wrap justify-center gap-4">
          <Link to="/contact" className="btn-primary" data-testid="cta-band-book-call">Book a Discovery Call</Link>
          <Link to="/contact" className="btn-ghost" data-testid="cta-band-consult">Request a Consultation</Link>
        </div>
      </div>
    </section>
  );
}
